import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  VaButton,
  VaModal,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { maskID } from 'platform/shared/dependents/shared/utils';
import { displayFileSize } from 'platform/utilities/ui';
import {
  claimantName,
  maskedIdentifier,
  shortDateOrBlank,
  statusField,
} from './CombinedHistoryFields';
import {
  SUBMISSION_TYPES,
  submissionPropType,
} from '../utilities/combinedHistory';

/** @typedef {import('../utilities/combinedHistory').CombinedHistorySubmission} Submission */

/** The id of the modal's Packet / Files heading. Only one modal shows. */
const FILES_HEADING_ID = 'submission-files-heading';

/**
 * Follows whether an element's content is taller than the element, as the
 * modal opens and as the window resizes.
 *
 * @param {React.RefObject<HTMLElement>} ref
 * @param {unknown} content Rechecks when this changes.
 * @returns {boolean} Whether the element scrolls.
 */
const useOverflows = (ref, content) => {
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      setOverflows(false);
      return undefined;
    }
    const check = () =>
      setOverflows(element.scrollHeight > element.clientHeight);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, content]);
  return overflows;
};

/**
 * Lets an element inside va-modal scroll by touch on iOS. There, va-modal's
 * scroll lock cancels any one-finger swipe on the modal once the modal is at
 * its top or bottom, which it always is when it fits on screen. A swipe that
 * stops at the element never reaches the lock. Only an element that scrolls
 * keeps its swipes, so a swipe on a short list still reaches the lock and
 * the page behind the modal stays put. Added as a native listener, since
 * React's touch events run after the lock's.
 *
 * @param {React.RefObject<HTMLElement>} ref
 * @param {boolean} scrolls Whether the element's content overflows it.
 */
const useOwnTouchScroll = (ref, scrolls) => {
  useEffect(() => {
    const element = ref.current;
    if (!element || !scrolls) return undefined;
    const keep = event => event.stopPropagation();
    element.addEventListener('touchmove', keep, { passive: true });
    return () => element.removeEventListener('touchmove', keep);
  }, [ref, scrolls]);
};

/**
 * @param {Submission} submission
 * @returns {boolean} Whether the row is an upload with files to list, so
 *   View never opens an empty modal.
 */
export const hasFiles = submission =>
  submission.type === SUBMISSION_TYPES.QUICK_SUBMIT &&
  submission.documents?.length > 0;

/**
 * @param {HTMLElement} view A View button's va-button.
 * @returns {HTMLButtonElement|null} The native button inside it, which is
 *   what takes focus.
 */
export const viewFilesFocusTarget = view =>
  view.shadowRoot?.querySelector('button') ?? null;

/**
 * The View button that opens an upload's files, as a VADS button, since it
 * opens a modal rather than a page. Its accessible name starts with its
 * visible text and names the submission. It takes focus when clicked, since
 * Safari doesn't focus a clicked button, so va-modal returns focus to it on
 * close. `data-submission-id` lets the history find it again after the
 * table and cards swap.
 *
 * @param {Object} props
 * @param {Submission} props.submission
 * @param {(submission: Submission) => void} props.onView Opens the modal.
 * @returns {JSX.Element}
 */
export const ViewFilesButton = ({ submission, onView }) => (
  <VaButton
    secondary
    text="View"
    data-testid="view-files"
    data-submission-id={submission.id}
    label={
      submission.confirmationNumber
        ? `View files for confirmation number ${submission.confirmationNumber}`
        : 'View files'
    }
    onClick={event => {
      viewFilesFocusTarget(event.currentTarget)?.focus();
      onView(submission);
    }}
  />
);

ViewFilesButton.propTypes = {
  submission: submissionPropType.isRequired,
  onView: PropTypes.func.isRequired,
};

/**
 * The files in one QuickSubmit upload, as a modal: the claimant and their
 * masked identifier, the status badge, the submission date, the type, and
 * the received date, then the packet number and a table of the files, which
 * scrolls inside its box when it runs long. Each file shows the upload's
 * date, since the upload has no date per file. va-modal returns focus to the
 * element that opened it when it closes.
 *
 * @param {Object} props
 * @param {Submission|null} props.submission The upload to show, or null
 *   while the modal is closed.
 * @param {() => void} props.onClose Closes the modal.
 * @returns {JSX.Element}
 */
const SubmissionFilesModal = ({ submission, onClose }) => {
  const status = submission ? statusField(submission) : null;
  const submittedOn = submission
    ? shortDateOrBlank(submission.submittedDate)
    : '';
  const documents = submission?.documents ?? [];
  const scrollRef = useRef(null);
  const scrolls = useOverflows(scrollRef, submission);
  useOwnTouchScroll(scrollRef, scrolls);

  return (
    <VaModal
      data-testid="submission-files-modal"
      className="submissions__files-modal"
      large
      visible={!!submission}
      label="Files"
      onCloseEvent={onClose}
    >
      {submission && (
        <div
          className="submissions__files-content"
          data-testid="submission-files-content"
        >
          {/* The title in the design's sans, bold at 20px. va-modal's own
              title is serif. The label above names the modal. */}
          <h2 className="vads-u-font-family--sans vads-u-font-size--lg vads-u-font-weight--bold vads-u-color--black vads-u-margin-top--0 vads-u-margin-bottom--2">
            Files
          </h2>
          <div className="vads-u-display--flex vads-u-flex-wrap--wrap vads-u-justify-content--space-between vads-u-align-items--flex-start vads-u-width--full">
            <div>
              <h3 className="vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--bold vads-u-color--black vads-u-margin-top--0 vads-u-margin-bottom--1">
                {claimantName(submission)}
                {/* Screen readers hear what the digits are, then "ending
                    with 7 1 8 9" rather than the asterisks. */}
                {maskedIdentifier(submission) && (
                  <>
                    {' '}
                    <span
                      className="vads-u-margin-left--1p5"
                      data-testid="masked-identifier"
                    >
                      <span className="vads-u-visibility--screen-reader">
                        SSN or VA file number{' '}
                      </span>
                      {maskID(maskedIdentifier(submission), '*****')}
                    </span>
                  </>
                )}
              </h3>
              {submittedOn && (
                <p className="vads-u-margin--0">
                  Submission date: {submittedOn}
                </p>
              )}
              {submission.type && (
                <p className="vads-u-margin--0">Type: {submission.type}</p>
              )}
              {status.receivedOn && (
                <p className="vads-u-margin--0">{status.receivedOn}</p>
              )}
            </div>
            {status.badge}
          </div>
          <h3
            id={FILES_HEADING_ID}
            className="vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--bold vads-u-color--black vads-u-margin-top--2p5 vads-u-margin-bottom--0p5"
          >
            Packet / Files
          </h3>
          <div className="submissions__files-box">
            {submission.cmPacketId != null && (
              <p className="vads-u-margin--0">
                <span className="vads-u-font-weight--bold">Packet:</span>{' '}
                {submission.cmPacketId}
              </p>
            )}
            {/* A list that scrolls is a region in the tab order, so a
                keyboard can scroll it too. It takes the heading's name. */}
            <div
              ref={scrollRef}
              className="submissions__files-scroll"
              data-testid="submission-files-scroll"
              {...(scrolls && {
                role: 'region',
                'aria-labelledby': FILES_HEADING_ID,
                tabIndex: 0,
              })}
            >
              <va-table
                data-testid="submission-files-table"
                table-type="borderless"
                full-width
                stacked={false}
              >
                <va-table-row slot="headers">
                  <span className="submissions__files-whole-words">
                    Filename
                  </span>
                  <span className="submissions__files-whole-words">
                    File size
                  </span>
                  <span className="submissions__files-whole-words">
                    Date uploaded
                  </span>
                </va-table-row>
                {documents.map((document, index) => (
                  <va-table-row key={`${index}-${document.fileName}`}>
                    <span className="submissions__files-name">
                      {document.fileName ?? 'Unnamed file'}
                    </span>
                    <span className="submissions__files-whole-words">
                      {displayFileSize(document.fileSize)}
                    </span>
                    <span className="submissions__files-whole-words">
                      {submittedOn}
                    </span>
                  </va-table-row>
                ))}
              </va-table>
            </div>
          </div>
          {/* Close on the right, as in the design. va-modal's own button
              sits on the left. */}
          <div className="vads-u-display--flex vads-u-justify-content--flex-end vads-u-margin-top--3">
            <VaButton
              className="submissions__files-close"
              data-testid="submission-files-close"
              secondary
              full-width
              text="Close"
              onClick={onClose}
            />
          </div>
        </div>
      )}
    </VaModal>
  );
};

SubmissionFilesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  submission: submissionPropType,
};

SubmissionFilesModal.defaultProps = {
  submission: null,
};

export default SubmissionFilesModal;
